import React, { useContext, useEffect } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { OBJECT_TYPE } from "common/constants";
import { formatCoordinates } from "common/utils/coordinates";
import { canDeleteDocumentUpload } from "common-client/utils/documentUploads";
import { useGetDocumentsForPropertyQuery } from "../../../generated/graphql";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import { AddressPanelContext } from "../AddressPanelContext";
import MissingProperty from "../MissingProperty";
import List from "./List";
import ListItem from "./ListItem";

interface Props extends RouteComponentProps {
  address?: Maybe<string>;
  latitude: number;
  longitude: number;
}

export default withRouter((props: Props) => {
  const { location, address, latitude, longitude } = props;
  const { loadingProperty, property } = useContext(AddressPanelContext);
  const { authorized, isPublic } = React.useContext(AuthContext);

  const propertyId = property?.id ?? null;

  const { data, loading, refetch } = useGetDocumentsForPropertyQuery({
    variables: { propertyId: propertyId ?? "", isPublic },
    fetchPolicy: "network-only",
    skip: !propertyId,
  });

  const isLoading = loadingProperty || loading;
  const documentUploads = data?.property?.documentUploads || [];

  useEffect(() => {
    track("Opened files tab", {
      address: property?.fullAddress,
    });
  }, [property]);

  if (isLoading) return <em>loading...</em>;
  if (!property) {
    return (
      <MissingProperty
        title="No files available"
        address={address}
        latitude={latitude}
        longitude={longitude}
      />
    );
  }

  return (
    <List
      object={{
        id: property.id,
        objectType: OBJECT_TYPE.PROPERTY,
        objectLabel:
          property.fullAddress ?? formatCoordinates({ latitude, longitude }),
      }}
    >
      {documentUploads.map(du => (
        <ListItem
          key={du.id}
          elevationCertificateId={du.elevationCertificate?.id}
          pathname={location.pathname}
          status={du.certificateUpload?.status ?? null}
          search={location.search}
          onUpdate={refetch}
          documentUpload={{
            ...du,
            property: property,
            isDeletable: canDeleteDocumentUpload({
              documentUpload: du,
              authorized,
            }),
          }}
        />
      ))}
    </List>
  );
});
